<script setup lang="ts">
/**
 * 產品範圍選擇（批107）。新增表單與編輯 modal 共用同一塊，**不再各寫一份**。
 *
 * ## 三態，不是兩態
 *
 * `product_keys` 的值域刻意有三種語意（`core/scope.py`）：
 *
 * | 值 | 意思 |
 * |---|---|
 * | `null` | **不限**（代理商帳號＝沿用公司範圍；有效範圖仍是帳號層 ∩ 公司層） |
 * | 有內容的陣列 | 只能經手這幾個產品 |
 * | `[]`（空陣列） | **暫停這個帳號的簽發權**——合法用法，不是「不限」 |
 *
 * 批97 之前 UI 只能表達前兩種（不勾＝送 null），於是「暫時停掉某人的簽發權」只能走 API。
 * 這裡用 radio 把三態攤開：`inherit`＝null、`explicit`＋勾選＝陣列、`explicit`＋零勾＝`[]`
 * （並顯示警語，避免有人以為零勾等於不限）。
 *
 * ## 代理商窗口的可勾選項限縮為公司範圍
 *
 * 批97 Bug 2 的教訓是「兩層都能自由勾 → 切換角色／公司後殘留的勾選被送出 → 後端 422
 * `scope_exceeds_reseller`，而畫面上完全看不出勾了什麼」。批107 重新開放帳號層收窄，
 * 但**選項只列公司範圍**——超出的組合在 UI 上根本選不出來，那條破口就不存在了。
 */
import { computed } from 'vue'

import { useDataStore } from '@/stores/data'

const store = useDataStore()

const props = defineProps<{
  /** `null`＝不限／沿用公司範圍；陣列＝指定（空陣列＝暫停簽發權）。 */
  modelValue: string[] | null
  /** 代理商窗口：可勾選項限縮為公司範圍，「不限」的文案改成「沿用公司範圍」。 */
  isReseller?: boolean
  /** 該窗口所屬公司的範圍（`null`／空＝公司不限）。 */
  companyKeys?: string[] | null
  disabled?: boolean
}>()
const emit = defineEmits<{ 'update:modelValue': [string[] | null] }>()

const mode = computed(() => (props.modelValue === null ? 'inherit' : 'explicit'))

/** 可勾的產品：代理商窗口限縮為公司範圍（公司不限＝全部產品）。 */
const choices = computed(() => {
  if (!props.isReseller) return store.products
  const keys = props.companyKeys
  if (!keys?.length) return store.products
  return store.products.filter((p) => keys.includes(p.key))
})

const picked = computed(() => props.modelValue ?? [])

function setMode(next: 'inherit' | 'explicit') {
  if (props.disabled) return
  // 切到「指定」時預帶目前的有效範圍（代理商＝公司範圍），比從零開始勾快得多；
  // 切回「不限」一律送 null。
  if (next === 'inherit') emit('update:modelValue', null)
  else emit('update:modelValue', choices.value.map((p) => p.key))
}

function toggle(key: string) {
  if (props.disabled || props.modelValue === null) return
  const cur = props.modelValue
  emit(
    'update:modelValue',
    cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key],
  )
}

/** 公司範圍的中文（代理商窗口的「沿用」說明用）。 */
const companyScopeText = computed(() => {
  const keys = props.companyKeys
  if (!keys?.length) return '不限'
  return keys.map((k) => store.productName(k)).join('、')
})
</script>

<template>
  <div class="scope">
    <div class="modes">
      <label class="rd">
        <input
          type="radio"
          :checked="mode === 'inherit'"
          :disabled="disabled"
          @change="setMode('inherit')"
        />
        <span>{{ isReseller ? `沿用公司範圍（${companyScopeText}）` : '不限（所有產品）' }}</span>
      </label>
      <label class="rd">
        <input
          type="radio"
          :checked="mode === 'explicit'"
          :disabled="disabled"
          @change="setMode('explicit')"
        />
        <span>指定產品</span>
      </label>
    </div>

    <div v-if="mode === 'explicit'" class="picks">
      <button
        v-for="p in choices"
        :key="p.key"
        type="button"
        class="pick"
        :class="{ on: picked.includes(p.key) }"
        :disabled="disabled"
        @click="toggle(p.key)"
      >
        {{ p.name }}
      </button>
      <p v-if="isReseller" class="subtle">只列得出這家公司的經手範圍——超出的組合本來就不生效。</p>
      <!-- 零勾＝`[]`＝暫停簽發權（合法但要講清楚，不然會被當成「還沒選」）。 -->
      <p v-if="!picked.length" class="warn-text">
        一個都沒勾＝<strong>暫停這個帳號的簽發權</strong>（不是「不限」）。
      </p>
    </div>
  </div>
</template>

<style scoped>
/* 樣式與 `AdminUsersView` 的膠囊維持同一套（`--border-strong` 邊、`--brand-50` 選中）
   ——同一個概念在兩個地方長得不一樣是最容易被抱怨的那種不一致。 */
.scope {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
}
.modes {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-4);
}
.rd {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--fs-13, 13px);
  color: var(--fg);
  cursor: pointer;
}
.rd input {
  accent-color: var(--brand-500);
}
.picks {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.pick {
  display: inline-flex;
  align-items: center;
  padding: 3px 11px;
  border: 1px solid var(--border-strong);
  border-radius: 999px;
  background: var(--bg);
  color: var(--fg-2);
  font-size: var(--fs-12);
  cursor: pointer;
  user-select: none;
}
.pick:hover:not(:disabled) {
  border-color: var(--brand-400);
}
.pick.on {
  background: var(--brand-50);
  border-color: var(--brand-400);
  color: var(--brand-700);
  font-weight: 600;
}
.pick:disabled {
  cursor: default;
  opacity: 0.6;
}
.picks .subtle,
.picks .warn-text {
  flex-basis: 100%;
  margin: 2px 0 0;
  font-size: var(--fs-12);
}
.subtle {
  color: var(--fg-3, var(--fg-2));
}
/* ★ 不可叫 `.warn`（scoped 樣式會套到子元件根節點；同 AdminUsersView 的註解）。 */
.warn-text {
  color: var(--warning);
}
</style>
